/**
 * Вызовы инструментов интерфейса в ответе (ADR-0011): не плашка с JSON, а
 * то, что человек понимает сразу.
 *
 * * `ui_suggest` — карточка «смотрите здесь»: название экрана и кнопка
 *   «Открыть»; переход с фильтрами и подсветкой блока;
 * * `ui_navigate` / `ui_set_filters` / `ui_highlight` — строка действия гида
 *   («Открыл „Работа цикла“», «Подсветил блок …»), с состоянием.
 */

import type { DynamicToolUIPart } from 'ai';
import { ArrowUpRight, Compass, Highlighter, SlidersHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../astra/ui/Icon';
import { openSuggestion, type UiCommand } from '../ai-surface/runtime';
import { defineMessages } from '../i18n/define';
import { tr } from '../i18n/runtime';
import { LazyAiWidget } from './widgets/LazyAiWidget';
import { isAiWidget } from './widgets/spec';
import { useT } from '../i18n/hooks';
import { screenTitle } from './screenTitles';

const m = defineMessages({
  linkFailed: { id: 'ai.uiTool.linkFailed', defaultMessage: 'Ссылка не собрана: {error}', description: 'Строка в ответе ИИ, когда не удалось собрать ссылку на экран; {error} — техническая причина' },
  choosing: { id: 'ai.uiTool.choosing', defaultMessage: 'Подбираю экран…', description: 'Строка в ответе ИИ, пока подбирается ссылка на экран (от первого лица)' },
  eyebrow: { id: 'ai.uiTool.eyebrow', defaultMessage: 'Смотрите на экране', description: 'Малая надпись над названием экрана в карточке-подсказке «смотрите здесь» в ответе ИИ' },
  open: { id: 'ai.uiTool.open', defaultMessage: 'Открыть', description: 'Кнопка в карточке-подсказке в ответе ИИ: перейти на предложенный экран; рядом стрелка' },
  navigate: { id: 'ai.uiTool.navigate', defaultMessage: 'Открываю «{target}»', description: 'Строка действия гида: агент открывает экран; {target} — название экрана' },
  navigateBlock: { id: 'ai.uiTool.navigateBlock', defaultMessage: 'Открываю «{target}», блок {block}', description: 'Строка действия гида: агент открывает экран и подсвечивает блок; {block} — код блока' },
  filters: { id: 'ai.uiTool.filters', defaultMessage: 'Меняю фильтры: {filters}', description: 'Строка действия гида: агент меняет фильтры; {filters} — перечень вида «культура=cotton, дата=2026-08-26»' },
  highlight: { id: 'ai.uiTool.highlight', defaultMessage: 'Подсвечиваю блок {block}', description: 'Строка действия гида: агент подсвечивает блок экрана; {block} — код блока' },
  notFound: { id: 'ai.uiTool.notFound', defaultMessage: '— блок сейчас не на экране', description: 'Дополнение к строке действия гида: подсвечиваемого блока нет на экране; начинается с тире' },
});

export const UI_TOOL_NAMES = new Set(['ui_suggest', 'ui_show_widget', 'ui_navigate', 'ui_set_filters', 'ui_highlight', 'ui_tour_plan', 'ui_ask_choice']);
/** Инструменты, которые исполняет браузер. */
export const CLIENT_UI_TOOLS = new Set(['ui_navigate', 'ui_set_filters', 'ui_highlight']);

interface LinkOutput {
  kind: 'link';
  href: string;
  /** Id экрана реестра: по нему — название на языке интерфейса. */
  page?: string;
  title: string;
  label: string;
  region?: string | null;
}

const isLink = (value: unknown): value is LinkOutput =>
  typeof value === 'object' && value !== null && (value as LinkOutput).kind === 'link' && typeof (value as LinkOutput).href === 'string';

function SuggestCard({ part }: { part: DynamicToolUIPart }) {
  const t = useT();
  const navigate = useNavigate();
  if (part.state === 'output-error') return <p className="agent-ui-step agent-ui-step--error">{t(m.linkFailed, { error: part.errorText })}</p>;
  const output: unknown = part.state === 'output-available' ? part.output : null;
  if (!isLink(output)) return <p className="agent-ui-step">{t(m.choosing)}</p>;
  // Название экрана сервер пишет по-русски (реестр); подпись модели — на языке ответа, а без
  // подписи сервер повторяет название экрана — тогда и она на языке интерфейса.
  const title = output.page ? screenTitle(output.page, output.title) : output.title;
  const label = output.label === output.title ? title : output.label;
  return (
    <div className="agent-link" data-tool="ui_suggest">
      <div className="agent-link__text">
        <span className="agent-link__eyebrow">{t(m.eyebrow)}</span>
        <span className="agent-link__title">{title}</span>
        <span className="agent-link__label">{label}</span>
      </div>
      <button
        type="button"
        className="btn btn--sm agent-link__open"
        onClick={() => void openSuggestion(output.href, output.region, (href) => navigate(href))}
      >
        {t(m.open)} <Icon icon={ArrowUpRight} size={16} />
      </button>
    </div>
  );
}

function stepText(part: DynamicToolUIPart): string {
  const input = (part.input ?? {}) as { command?: UiCommand; page?: string; region?: string };
  const command = input.command;
  const page = command?.page ?? input.page ?? '';
  const target = command?.title ? screenTitle(page, command.title) : page;
  if (part.toolName === 'ui_navigate') return command?.highlight ? tr(m.navigateBlock, { target, block: command.highlight }) : tr(m.navigate, { target });
  if (part.toolName === 'ui_set_filters') {
    const filters = Object.entries(command?.filters ?? {}).map(([k, v]) => `${k}=${v || '—'}`).join(', ');
    return tr(m.filters, { filters: filters || '—' });
  }
  return tr(m.highlight, { block: command?.region ?? input.region ?? '' });
}

const ICONS = { ui_navigate: Compass, ui_set_filters: SlidersHorizontal, ui_highlight: Highlighter } as const;

export function UiToolCard({ part }: { part: DynamicToolUIPart }) {
  const t = useT();
  // Отказ проверки (неизвестная страница, блок, второй переход) — ошибка
  // МОДЕЛИ: она исправляет вызов; участнику отказ не показывается (6al.23).
  if (part.state === 'output-error') return null;
  if (part.toolName === 'ui_suggest') return <SuggestCard part={part} />;
  if (part.toolName === 'ui_show_widget') {
    const output: unknown = part.state === 'output-available' ? part.output : null;
    return isAiWidget(output) ? <LazyAiWidget spec={output} /> : <p className="agent-ui-step">{t(m.choosing)}</p>;
  }
  if (!CLIENT_UI_TOOLS.has(part.toolName)) return null;
  const icon = ICONS[part.toolName as keyof typeof ICONS] ?? Compass;
  const output = part.state === 'output-available' ? (part.output as { ok?: boolean; available?: string[] }) : null;
  const notFound = output && output.ok === false;
  return (
    <p className="agent-ui-step" data-tool={part.toolName} data-state={part.state}>
      <Icon icon={icon} size={16} />
      <span>{stepText(part)}</span>
      {notFound ? <span>{t(m.notFound)}</span> : null}
    </p>
  );
}
